import { useState } from "react";
import type { ProgramClip } from "./types";

type ProgramMarkDoneModalProps = {
  clip: ProgramClip | undefined;
  title?: string;
  notesRequired?: boolean;
  onSave: (painScore: number | null, notes: string | null) => void;
  onClose: () => void;
};

export default function ProgramMarkDoneModal({
  clip,
  title,
  notesRequired = false,
  onSave,
  onClose,
}: ProgramMarkDoneModalProps) {
  const [showPainSlider, setShowPainSlider] = useState(false);
  const [painScore, setPainScore] = useState(5);
  const [notes, setNotes] = useState("");
  const [notesError, setNotesError] = useState<string | null>(null);

  const handleSave = () => {
    if (notesRequired && !notes.trim()) {
      setNotesError("Notes are required.");
      return;
    }

    setNotesError(null);
    onSave(showPainSlider ? painScore : null, notes.trim() || null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-bg-primary rounded-t-2xl sm:rounded-2xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-text-primary">
          {title ?? clip?.video?.title}
        </h3>

        {/* Pain score — hidden by default, opt-in */}
        {showPainSlider ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm text-text-secondary">
                Pain / discomfort: {painScore}
              </label>
              <button
                onClick={() => setShowPainSlider(false)}
                className="text-xs text-text-tertiary hover:text-text-secondary"
              >
                Remove
              </button>
            </div>
            <input
              type="range"
              min={0}
              max={10}
              value={painScore}
              onChange={(e) => setPainScore(Number(e.target.value))}
              className="w-full accent-indigo-500"
            />
            <div className="flex justify-between text-xs text-text-tertiary">
              <span>0 – None</span>
              <span>10 – Severe</span>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowPainSlider(true)}
            className="w-full py-2 text-sm text-text-secondary border border-dashed border-border rounded-lg hover:bg-bg-tertiary transition-colors"
          >
            + Log pain score (optional)
          </button>
        )}

        {/* Notes */}
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            if (notesError) setNotesError(null);
          }}
          placeholder={
            notesRequired
              ? "Notes (required) — explain the assessment, symptoms, or evidence"
              : "Notes (optional) — e.g. felt tight, used lighter weight"
          }
          className="w-full bg-bg-tertiary text-text-primary text-sm rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500 border border-border"
        />
        {notesError && <p className="text-xs text-red-500">{notesError}</p>}

        <button
          onClick={handleSave}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold text-sm transition-colors"
        >
          Mark Done
        </button>

        <button
          onClick={onClose}
          className="w-full py-2 text-sm text-text-tertiary hover:text-text-secondary"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
